import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, User, LoginRequest, UserRole } from '../types/api';
import { apiClient } from '../lib/api';

// Auth Context
interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updateData: {
    username?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
  }) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Reducer
type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, loading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  error: null
};

// Auth Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = apiClient.getToken();
      if (token) {
        dispatch({ type: 'AUTH_LOADING' });
        try {
          const user = await apiClient.getCurrentUser();
          dispatch({ 
            type: 'AUTH_SUCCESS', 
            payload: { user, token } 
          });
        } catch (error) {
          // Token is invalid, clear it
          apiClient.setToken(null);
          dispatch({ 
            type: 'AUTH_ERROR', 
            payload: 'Session expired. Please login again.' 
          });
        }
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      const response = await apiClient.login(credentials);
      
      if (response.success && response.user && response.token) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.user as User,
            token: response.token
          }
        });
      } else {
        dispatch({
          type: 'AUTH_ERROR',
          payload: response.error || 'Login failed'
        });
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Login failed'
      });
    }
  }, []);

  const logout = useCallback(async () => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  const updateProfile = useCallback(async (updateData: {
    username?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
  }) => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      const response = await apiClient.updateProfile(updateData);
      
      if (response.success) {
        // Fetch updated user data
        const updatedUser = await apiClient.getCurrentUser();
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      } else {
        dispatch({
          type: 'AUTH_ERROR',
          payload: response.error || 'Profile update failed'
        });
        throw new Error(response.error || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    updateProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Permission checking hooks
export const usePermissions = () => {
  const { user } = useAuth();
  
  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canView = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.OPERATOR, UserRole.VIEWER]);
  };

  const canOperate = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.OPERATOR]);
  };

  const canAdmin = (): boolean => {
    return hasRole(UserRole.ADMIN);
  };

  return {
    hasRole,
    hasAnyRole,
    canView,
    canOperate,
    canAdmin,
    user
  };
};